import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";

import BasicCRUD from "./../core/basic-crud";
import FadeAlert from "../core/fade-alert";
import { socket } from "../../socket";
import { useAuth } from "../../AuthContext";
import { BACKEND_HTTP_URL, BACKEND_PATH } from "../../const";
import resolveUserRole from "../utils/resolveUserRole";

// -------- i18n --------
const dict = {
    en: {
        // page
        title: "Contacts",
        subTitle: "add / edit contacts",
        homeURL: "/dashboard",
        addTitle: "Add Contact",
        editTitle: "Edit Contact",

        // columns
        colGroupName: "Group Name",
        colRelations: "Relations",

        // form
        tabMain: "Main",
        fieldGroupName: "Group Name",
        fieldEmails: "Notification emails",
        fieldEmailsHint: "Add up to 10 additional emails that will receive notifications together with this group.",
        addEmail: "Add email",
        removeEmail: "Remove",
        noEmails: "No emails attached yet.",
        fieldWebhooks: "Webhook URLs",
        fieldWebhooksHint: "Add up to 3 webhook endpoints for automated callbacks.",
        addWebhook: "Add webhook",
        removeWebhook: "Remove",
        noWebhooks: "No webhooks attached yet.",
        fieldSlackGroups: "Slack channels",
        fieldSlackGroupsHint: "Pick up to 3 Slack channels and route alerts to the selected conversations.",
        noSlackGroups: "No Slack channels connected yet.",
        fieldTelegramGroups: "Telegram chats",
        fieldTelegramGroupsHint: "Pick up to 3 Telegram chats to receive notifications via the bot.",
        noTelegramGroups: "No Telegram chats connected yet.",
        connectSlack: "Connect via Slack",
        connectSlackInProgress: "Awaiting Slack…",
        slackSyncError: "Failed to connect Slack channel. Please try again.",
        slackConnectedLabel: "Available Slack channels: {count}",
        connectTelegram: "Connect via Telegram",
        connectTelegramInProgress: "Waiting for Telegram…",
        telegramLinkError: "Failed to create Telegram link. Please try again.",
        telegramConnectedLabel: "Available Telegram chats: {count}",
        memberViewOnly: "Members can only view contact groups.",

        // actions
        edit: "Edit",
        delete: "Delete",

        // delete dialog
        delAlert: "Deleting a contact group cannot be undone.",
        delText: "To confirm deletion, enter the name of the contact group in the next input field",
        delTitle: 'Delete contact group: "{name}"',

        // toasts
        toastEntity: "Contact",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "Контакты",
        subTitle: "добавление / редактирование контактов",
        homeURL: "/dashboard",
        addTitle: "Добавить контакт",
        editTitle: "Редактировать контакт",

        colGroupName: "Название группы",
        colRelations: "Связи",

        tabMain: "Основное",
        fieldGroupName: "Название группы",
        fieldEmails: "Уведомления на email",
        fieldEmailsHint: "Добавьте до 10 адресов, которые будут получать уведомления вместе с этой группой.",
        addEmail: "Добавить email",
        removeEmail: "Удалить",
        noEmails: "Пока нет прикреплённых адресов.",
        fieldWebhooks: "Webhook-адреса",
        fieldWebhooksHint: "Добавьте до 3 webhook-адресов для автоматических уведомлений.",
        addWebhook: "Добавить webhook",
        removeWebhook: "Удалить",
        noWebhooks: "Webhook-адреса ещё не добавлены.",
        fieldSlackGroups: "Каналы Slack",
        fieldSlackGroupsHint: "Выберите до 3 каналов Slack, чтобы отправлять туда уведомления.",
        noSlackGroups: "Каналы Slack ещё не подключены.",
        fieldTelegramGroups: "Telegram-чаты",
        fieldTelegramGroupsHint: "Подключите до 3 Telegram-чатов, чтобы бот отправлял туда уведомления.",
        noTelegramGroups: "Telegram-чаты ещё не подключены.",
        connectSlack: "Подключить через Slack",
        connectSlackInProgress: "Ожидаем Slack…",
        slackSyncError: "Не удалось подключить канал Slack. Попробуйте ещё раз.",
        slackConnectedLabel: "Доступно Slack-каналов: {count}",
        connectTelegram: "Подключить через Telegram",
        connectTelegramInProgress: "Ожидаем Telegram…",
        telegramLinkError: "Не удалось создать ссылку Telegram. Попробуйте ещё раз.",
        telegramConnectedLabel: "Доступно Telegram-чатов: {count}",
        memberViewOnly: "У участников только просмотр списков контактов.",

        edit: "Редактировать",
        delete: "Удалить",

        delAlert: "Удаление группы контактов необратимо.",
        delText: "Чтобы подтвердить удаление, введите название группы контактов в следующее поле ввода",
        delTitle: 'Удалить группу контактов: «{name}»',

        toastEntity: "Контакт",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
    },

    ua: {
        title: "Контакти",
        subTitle: "додавання / редагування контактів",
        homeURL: "/dashboard",
        addTitle: "Додати контакт",
        editTitle: "Редагувати контакт",

        colGroupName: "Назва групи",
        colRelations: "Зв’язки",

        tabMain: "Основне",
        fieldGroupName: "Назва групи",
        fieldEmails: "Email-сповіщення",
        fieldEmailsHint: "Додайте до 10 адрес, що отримуватимуть сповіщення разом із цією групою.",
        addEmail: "Додати email",
        removeEmail: "Видалити",
        noEmails: "Адреси ще не додані.",
        fieldWebhooks: "Webhook-адреси",
        fieldWebhooksHint: "Додайте до 3 webhook-адрес для автоматичних викликів.",
        addWebhook: "Додати webhook",
        removeWebhook: "Видалити",
        noWebhooks: "Webhook-адреси ще не додані.",
        fieldSlackGroups: "Канали Slack",
        fieldSlackGroupsHint: "Оберіть до 3 каналів Slack, щоб отримувати в них сповіщення.",
        noSlackGroups: "Канали Slack ще не підключені.",
        fieldTelegramGroups: "Telegram-чати",
        fieldTelegramGroupsHint: "Підключіть до 3 Telegram-чатів, щоб бот надсилав туди сповіщення.",
        noTelegramGroups: "Telegram-чати ще не підключені.",
        connectSlack: "Підключити через Slack",
        connectSlackInProgress: "Очікуємо Slack…",
        slackSyncError: "Не вдалося підключити канал Slack. Спробуйте ще раз.",
        slackConnectedLabel: "Доступно Slack-каналів: {count}",
        connectTelegram: "Підключити через Telegram",
        connectTelegramInProgress: "Очікуємо Telegram…",
        telegramLinkError: "Не вдалося створити посилання Telegram. Спробуйте ще раз.",
        telegramConnectedLabel: "Доступно Telegram-чатів: {count}",
        memberViewOnly: "У учасників лише перегляд списку контактів.",

        edit: "Редагувати",
        delete: "Видалити",

        delAlert: "Видалення групи контактів неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву групи контактів у наступне поле вводу",
        delTitle: 'Видалити групу контактів: «{name}»',

        toastEntity: "Контакт",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// -------- component --------
const EMAIL_LIMIT = 10;
const WEBHOOK_LIMIT = 3;
const SLACK_LIMIT = 3;
const TELEGRAM_LIMIT = 3;

const parseListValue = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed;
            }
        } catch (err) {
            // ignore
        }
        return trimmed.split(/[,;\u000a\u000d]/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
};

const DynamicListField = ({
    data = {},
    fieldsChange,
    field,
    label,
    hint,
    addLabel,
    removeLabel,
    emptyText,
    limit = EMAIL_LIMIT,
}) => {
    const fieldKey = field?.key || "extra_emails";
    const fallbackKey = field?.fallbackKey ?? fieldKey;
    const getInitialItems = () => parseListValue(data[fieldKey] ?? data[fallbackKey]);
    const [items, setItems] = useState(getInitialItems);
    const lastSerializedRef = useRef(JSON.stringify(getInitialItems()));

    const propagateChange = useCallback((list) => {
        lastSerializedRef.current = JSON.stringify(list);
        if (typeof fieldsChange === "function") {
            fieldsChange({ target: { value: list } }, fieldKey);
        }
    }, [fieldsChange, fieldKey]);

    useEffect(() => {
        const normalized = parseListValue(data[fieldKey] ?? data[fallbackKey]);
        const serialized = JSON.stringify(normalized);
        if (serialized !== lastSerializedRef.current) {
            setItems(normalized);
            propagateChange(normalized);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data[fieldKey] ?? data[fallbackKey] ?? [])]);

    const handleItemChange = (index, value) => {
        const next = [...items];
        next[index] = value;
        setItems(next);
        propagateChange(next);
    };

    const handleRemove = (index) => {
        const next = items.filter((_, idx) => idx !== index);
        setItems(next);
        propagateChange(next);
    };

    const handleAdd = () => {
        if (items.length >= limit) return;
        const next = [...items, ""];
        setItems(next);
        propagateChange(next);
    };

    const canAdd = items.length < limit;

    return (
        <div className="mb-4">
            {label && <label className="form-label fw-semibold">{label}</label>}
            {hint && <p className="text-muted small mb-2">{hint}</p>}

            {items.length === 0 && (
                <div className="text-muted small mb-2">{emptyText}</div>
            )}

            {items.map((value, index) => (
                <div className="input-group mb-2" key={`item-${field?.key || "list"}-${index}`}>
                    <input
                        type={field?.inputType || "text"}
                        className="form-control"
                        placeholder={field?.placeholder ? `${field.placeholder} ${index + 1}` : ""}
                        value={value}
                        onChange={(event) => handleItemChange(index, event.target.value)}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleRemove(index)}
                    >
                        {removeLabel}
                    </button>
                </div>
            ))}

            <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleAdd}
                disabled={!canAdd}
            >
                {addLabel} ({items.length}/{limit})
            </button>
        </div>
    );
};

const IntegrationGroupsField = ({
    data = {},
    fieldsChange,
    field,
    options = [],
    limit = 3,
    label,
    hint,
    emptyText,
    onConnect,
    connecting = false,
    connectLabel,
    connectingLabel,
    availabilityLabel,
}) => {
    const fieldKey = field?.key || "extra_slack_groups";
    const fallbackKey = field?.fallbackKey ?? fieldKey;
    const parseSelected = (value) => parseListValue(value).map((id) => `${id}`);
    const getInitialSelected = () => parseSelected(data[fieldKey] ?? data[fallbackKey]);
    const [selected, setSelected] = useState(getInitialSelected);
    const lastSerializedRef = useRef(JSON.stringify(getInitialSelected()));

    const propagateChange = useCallback((list) => {
        lastSerializedRef.current = JSON.stringify(list);
        if (typeof fieldsChange === "function") {
            fieldsChange({ target: { value: list } }, fieldKey);
        }
    }, [fieldsChange, fieldKey]);

    useEffect(() => {
        const normalized = parseSelected(data[fieldKey] ?? data[fallbackKey]);
        const serialized = JSON.stringify(normalized);
        if (serialized !== lastSerializedRef.current) {
            setSelected(normalized);
            propagateChange(normalized);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data[fieldKey] ?? data[fallbackKey] ?? [])]);

    const toggle = (id) => {
        setSelected((prev) => {
            let next = [...prev];
            if (next.includes(id)) {
                next = next.filter((item) => item !== id);
            } else {
                if (next.length >= limit) {
                    return prev;
                }
                next.push(id);
            }
            propagateChange(next);
            return next;
        });
    };

    const isDisabled = (id) =>
        !selected.includes(id) && selected.length >= limit;

    return (
        <div className="mb-4">
            {label && <label className="form-label fw-semibold">{label}</label>}
            {hint && <p className="text-muted small mb-2">{hint}</p>}

            {onConnect && (
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={onConnect}
                        disabled={connecting}
                    >
                        {connecting ? (connectingLabel || connectLabel) : connectLabel}
                    </button>
                    {availabilityLabel && (
                        <span className="text-muted small">{availabilityLabel}</span>
                    )}
                </div>
            )}

            {options.length === 0 && (
                <div className="text-muted small">{emptyText}</div>
            )}

            {options.length > 0 && (
                <div className="d-flex flex-column gap-2">
                    {options.map((option) => {
                        const optionId = `${option.id}`;
                        const primaryLabel = option.name || option.channel || `Slack group #${optionId}`;
                        const secondaryLabel = option.team_name || option.workspace_name || ((option.channel && option.channel !== primaryLabel) ? option.channel : "");
                        return (
                            <div className="form-check" key={`slack-option-${optionId}`}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`slack-${optionId}`}
                                    checked={selected.includes(optionId)}
                                    disabled={isDisabled(optionId)}
                                    onChange={() => toggle(optionId)}
                                />
                                <label className="form-check-label" htmlFor={`slack-${optionId}`}>
                                    <div className="fw-semibold">{primaryLabel}</div>
                                    {secondaryLabel && (
                                        <div className="text-muted small">{secondaryLabel}</div>
                                    )}
                                </label>
                            </div>
                        );
                    })}
                    <div className="text-muted small">
                        {selected.length}/{limit}
                    </div>
                </div>
            )}
        </div>
    );
};

const Contacts = ({ lang: langProp, ...props }) => {
    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const canManageContacts = currentUserRole !== "member";
    const backendBase = useMemo(() => {
        const normalized = BACKEND_HTTP_URL.replace(/\/$/, "");

        if (!BACKEND_PATH || !BACKEND_PATH.includes("/api") || normalized.endsWith("/api")) {
            return normalized;
        }

        try {
            const url = new URL(normalized);
            url.pathname = `${url.pathname.replace(/\/$/, "")}/api`;
            return url.toString().replace(/\/$/, "");
        } catch (err) {
            return `${normalized}/api`;
        }
    }, []);
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "contacts";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [slackGroups, setSlackGroups] = useState([]);
    const [slackSyncing, setSlackSyncing] = useState(false);
    const slackPopupRef = useRef(null);
    const [telegramGroups, setTelegramGroups] = useState([]);
    const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
    const telegramPollingRef = useRef(null);

    const formatRelations = (data) => {
        const relations = Number(data['relations'] || 0);
        return (
            <span className="badge bg-light text-dark fw-semibold">
                {relations}
            </span>
        );
    };

    const fetchSlackGroups = useCallback(() => {
        if (!user?.token || !user?.company_id) return;

        socket.emit("slack_groups:list", {
            token: user.token,
            company_id: user.company_id,
            page: 1,
            items_count: 100,
        });
    }, [user?.token, user?.company_id]);

    const fetchTelegramGroups = useCallback(() => {
        if (!user?.token || !user?.company_id) return;

        socket.emit("telegram_groups:list", {
            token: user.token,
            company_id: user.company_id,
            page: 1,
            items_count: 100,
        });
    }, [user?.token, user?.company_id]);

    useEffect(() => {
        const handler = (payload = {}) => {
            setSlackGroups(payload.data || []);
        };

        socket.on("slack_groups:list-data", handler);
        fetchSlackGroups();

        return () => {
            socket.off("slack_groups:list-data", handler);
        };
    }, [fetchSlackGroups]);

    useEffect(() => {
        const handler = (payload = {}) => {
            setTelegramGroups(payload.data || []);
        };

        socket.on("telegram_groups:list-data", handler);
        fetchTelegramGroups();

        return () => {
            socket.off("telegram_groups:list-data", handler);
        };
    }, [fetchTelegramGroups]);

    useEffect(() => {
        const handler = (event) => {
            if (typeof event.data !== "string") {
                return;
            }

            if (event.data === "slack-sync-success") {
                setSlackSyncing(false);
                fetchSlackGroups();
            }

            if (event.data === "slack-sync-error") {
                setSlackSyncing(false);
                // eslint-disable-next-line no-alert
                alert(t.slackSyncError);
            }
        };

        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [fetchSlackGroups, t.slackSyncError]);

    useEffect(() => {
        if (!slackSyncing) {
            return undefined;
        }

        const timer = setInterval(() => {
            if (slackPopupRef.current && slackPopupRef.current.closed) {
                slackPopupRef.current = null;
                setSlackSyncing(false);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [slackSyncing]);

    const openSlackAuth = useCallback(() => {
        if (!user?.token) {
            return;
        }

        const popupUrl = `${backendBase}/slack/oauth/start?token=${encodeURIComponent(user.token)}`;
        const width = 640;
        const height = 720;
        const left = typeof window !== "undefined" ? window.screenX + (window.outerWidth - width) / 2 : 0;
        const top = typeof window !== "undefined" ? window.screenY + (window.outerHeight - height) / 2 : 0;
        const popup = window.open(
            popupUrl,
            "slack-oauth",
            `width=${width},height=${height},left=${left},top=${top}`
        );

        if (popup) {
            slackPopupRef.current = popup;
            popup.focus();
            setSlackSyncing(true);
        }
    }, [backendBase, user?.token]);

    const stopTelegramPolling = useCallback(() => {
        if (telegramPollingRef.current) {
            clearInterval(telegramPollingRef.current.intervalId);
            clearTimeout(telegramPollingRef.current.timeoutId);
            telegramPollingRef.current = null;
        }
    }, []);

    const beginTelegramPolling = useCallback(() => {
        stopTelegramPolling();
        fetchTelegramGroups();
        const intervalId = setInterval(fetchTelegramGroups, 7000);
        const timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            if (telegramPollingRef.current?.timeoutId === timeoutId) {
                telegramPollingRef.current = null;
            }
        }, 60000);
        telegramPollingRef.current = { intervalId, timeoutId };
    }, [fetchTelegramGroups, stopTelegramPolling]);

    const openTelegramConnect = useCallback(async () => {
        if (!user?.token) {
            return;
        }

        setTelegramLinkLoading(true);
        try {
            const response = await fetch(
                `${backendBase}/telegram/connect-link?token=${encodeURIComponent(user.token)}`
            );
            const payload = await response.json();
            if (payload?.status === 1 && payload.link) {
                window.open(payload.link, "_blank", "noopener");
                beginTelegramPolling();
            } else {
                // eslint-disable-next-line no-alert
                alert(t.telegramLinkError);
            }
        } catch (err) {
            console.error("telegram connect failed", err);
            // eslint-disable-next-line no-alert
            alert(t.telegramLinkError);
        } finally {
            setTelegramLinkLoading(false);
        }
    }, [backendBase, user?.token, t.telegramLinkError, beginTelegramPolling]);

    useEffect(() => () => stopTelegramPolling(), [stopTelegramPolling]);

    const columns = [
        { label: t.colGroupName, key: "group_name" },
        { label: t.colRelations, key: "relations", format: formatRelations },
    ];

    const renderContactCard = (item, helpers) => {
        const actionsList = helpers.actions || [];
        const handleAction = (actionName) => helpers.tableAction?.(actionName, item);

        return (
            <div className="card h-100 shadow-sm border">
                <div className="card-body d-flex flex-column">
                    <h5 className="mb-2">{item.group_name}</h5>
                    <div className="text-muted mb-4">
                        {t.colRelations}: <span className="badge bg-secondary-subtle text-secondary">{item.relations ?? 0}</span>
                    </div>

                    {actionsList.length > 0 && (
                        <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                            {actionsList.map((action) => (
                                <button
                                    key={action.name}
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title={action.label}
                                    onClick={() => handleAction(action.name)}
                                >
                                    <i className={action.icon || "ti ti-dots"}></i>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const fieldsVersion = useMemo(
        () => JSON.stringify({
            slackSelected: parseListValue(fieldsData.extra_slack_groups || []).map((id) => `${id}`).sort(),
            slackOptions: (slackGroups || []).map((item) => item.id).sort(),
            telegramSelected: parseListValue(fieldsData.extra_telegram_groups || []).map((id) => `${id}`).sort(),
            telegramOptions: (telegramGroups || []).map((item) => item.id).sort(),
        }),
        [fieldsData.extra_slack_groups, fieldsData.extra_telegram_groups, slackGroups, telegramGroups]
    );

    const fields = useMemo(() => {
        const slackStatsLabel = t.slackConnectedLabel.replace("{count}", slackGroups.length || 0);
        const telegramStatsLabel = t.telegramConnectedLabel
            ? t.telegramConnectedLabel.replace("{count}", telegramGroups.length || 0)
            : "";
        return [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [{ label: t.fieldGroupName, key: "group_name", default_value: "", showLabel: "top" }],
                    [
                        {
                            label: t.fieldEmails,
                            key: "extra_emails",
                            default_value: [],
                            type: "custom",
                            fn: (props) => (
                                <DynamicListField
                                    {...props}
                                    field={{ key: "extra_emails", fallbackKey: "extra_emails", inputType: "email", placeholder: "email" }}
                                    label={t.fieldEmails}
                                    hint={t.fieldEmailsHint}
                                    addLabel={t.addEmail}
                                    removeLabel={t.removeEmail}
                                    emptyText={t.noEmails}
                                    limit={EMAIL_LIMIT}
                                />
                            ),
                        },
                        {
                            label: t.fieldWebhooks,
                            key: "extra_webhooks",
                            default_value: [],
                            type: "custom",
                            fn: (props) => (
                                <DynamicListField
                                    {...props}
                                    field={{ key: "extra_webhooks", fallbackKey: "extra_webhooks", inputType: "url", placeholder: "https://..." }}
                                    label={t.fieldWebhooks}
                                    hint={t.fieldWebhooksHint}
                                    addLabel={t.addWebhook}
                                    removeLabel={t.removeWebhook}
                                    emptyText={t.noWebhooks}
                                    limit={WEBHOOK_LIMIT}
                                />
                            ),
                        },
                    ],
                    [
                        {
                            label: t.fieldSlackGroups,
                            key: "extra_slack_groups",
                            default_value: [],
                            type: "custom",
                            fn: (props) => (
                                <IntegrationGroupsField
                                    {...props}
                                    field={{ key: "extra_slack_groups", fallbackKey: "extra_slack_groups" }}
                                    options={slackGroups}
                                    label={t.fieldSlackGroups}
                                    hint={t.fieldSlackGroupsHint}
                                    emptyText={t.noSlackGroups}
                                    limit={SLACK_LIMIT}
                                    onConnect={openSlackAuth}
                                    connecting={slackSyncing}
                                    connectLabel={t.connectSlack}
                                    connectingLabel={t.connectSlackInProgress}
                                    availabilityLabel={slackStatsLabel}
                                />
                            ),
                        },
                        {
                            label: t.fieldTelegramGroups,
                            key: "extra_telegram_groups",
                            default_value: [],
                            type: "custom",
                            fn: (props) => (
                                <IntegrationGroupsField
                                    {...props}
                                    field={{ key: "extra_telegram_groups", fallbackKey: "extra_telegram_groups" }}
                                    options={telegramGroups}
                                    label={t.fieldTelegramGroups}
                                    hint={t.fieldTelegramGroupsHint}
                                    emptyText={t.noTelegramGroups}
                                    limit={TELEGRAM_LIMIT}
                                    onConnect={openTelegramConnect}
                                    connecting={telegramLinkLoading}
                                    connectLabel={t.connectTelegram}
                                    connectingLabel={t.connectTelegramInProgress}
                                    availabilityLabel={telegramStatsLabel}
                                />
                            ),
                        },
                    ],
                ],
            },
        ];
    }, [
        t,
        slackGroups,
        slackSyncing,
        openSlackAuth,
        telegramGroups,
        telegramLinkLoading,
        openTelegramConnect,
    ]);

    const actions = useMemo(() => (canManageContacts ? [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        { name: "delete", label: t.delete, controller: "crud", icon: "ti ti-trash" },
    ] : []), [canManageContacts, t]);

    const disableConfig = canManageContacts ? undefined : { add: { disable: true, title: t.memberViewOnly } };

    return (
        <>
            <BasicCRUD
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                editTitle={t.editTitle}
                object={object}
                disable={disableConfig}
                columns={columns}
                fields={fields}
                fieldsVersion={fieldsVersion}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'group_name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderContactCard}
                cardLayout={{ columnClass: "col-12 col-md-6 col-xl-4 col-xxl-3" }}
                groupActionsInBlocks={false}
            >
                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default Contacts;
